import { type ModuleApiInfo } from "@client/content/selectors";
import { Loading } from "@client/controls/Loading/component";
import { unwrapResponse, unwrapResponsePromise } from "@client/lib/api";
import { usePromise } from "@client/lib/hooks";
import { type WithId } from "@client/types";
import {
  NETWORK_GENEALOGY_DOWNLINE_REPORT_ID,
  NETWORK_GENEALOGY_UPLINE_REPORT_ID
} from "@common/components/Report/Provider/Backend";
import NetworkVolumeReportProvider, {
  NETWORK_VOLUME_REPORT_ID,
  type NetworkVolumeReportConfig,
  type NetworkVolumeReportData
} from "@common/components/Report/Provider/Static/NetworkVolume";
import Report from "@common/components/Report/Report";
import { DynamicReport } from "@common/components/Report/component";
import { type ReportData } from "@common/components/Report/types";
import { useI18n } from "@common/hooks/i18n";
import getOptions from "@common/lib/options";
import AffiliateGenealogy from "@common/modules/affiliate/Analytics/Genealogy";
import { getApi } from "@common/modules/network/api";
import { SPONSOR_GENEALOGY_ID } from "@common/modules/network/lib";
import Network from "@common/modules/network/types";
import React from "react";
import api from "../../api";
import { BackButton, BackContainer } from "../Back";

export type MapProps = WithId & {
  apiInfo: ModuleApiInfo;
  config?: NetworkVolumeReportConfig;
  cycleOptions: Network.Cycles.Api.CycleOption[];
  cycleEndDate: string;
  reportId: string;
};

export type ActionProps = {
  onBackClick: () => void;
};

export default (props: ActionProps & MapProps) => {
  const i18n = useI18n();

  const options = React.useMemo(() => getOptions(i18n), [i18n]);

  const getData = React.useCallback(
    async (cycleEndDate: string): Promise<NetworkVolumeReportData> => {
      const [aggregates, profile] = await Promise.all([
        api.getCycleAggregates(props.apiInfo, cycleEndDate),
        api.getAffiliateDashboard(props.apiInfo, cycleEndDate)
      ]);
      return {
        items: unwrapResponse(aggregates),
        currencyCode: unwrapResponse(profile).currencyCode
      };
    },
    [props.apiInfo]
  );

  const [ranks] = usePromise({
    fn: options.getCustomOptions<Network.Api.RankOptionItem>,
    args: ["mlm", "ranks", undefined, "memory"],
    shouldLoad: true
  });

  if (
    props.reportId === NETWORK_GENEALOGY_DOWNLINE_REPORT_ID ||
    props.reportId === NETWORK_GENEALOGY_UPLINE_REPORT_ID
  ) {
    return (
      <AffiliateGenealogy
        cycleEndDate={props.cycleEndDate}
        getItems={(cycleEndDate) =>
          unwrapResponsePromise(
            props.reportId === NETWORK_GENEALOGY_DOWNLINE_REPORT_ID
              ? api.getDownline(props.apiInfo, {
                  cycleEndDate,
                  genealogyTypeId: SPONSOR_GENEALOGY_ID,
                  isMember: true,
                  maxLevels: 3
                })
              : api.getUpline(props.apiInfo, {
                  cycleEndDate,
                  genealogyTypeId: SPONSOR_GENEALOGY_ID
                })
          )
        }
        getDetail={(item) => getApi(i18n).getGenealogyDetail(item.id, props.cycleEndDate)}
        title={<BackButton onClick={props.onBackClick} />}
      />
    );
  }

  if (props.reportId === NETWORK_VOLUME_REPORT_ID) {
    if (!ranks) return <Loading />;

    return (
      <BackContainer onBackClick={props.onBackClick}>
        <NetworkVolumeReportProvider
          id={props.reportId}
          config={props.config!}
          cycleOptions={props.cycleOptions}
          cycleEndDate={props.cycleEndDate}
          getData={getData}
          ranks={ranks}
        >
          {(data: ReportData) => (
            <Report alwaysShowRunButton={false} rootId={props.reportId} useUrlState={false} {...data} />
          )}
        </NetworkVolumeReportProvider>
      </BackContainer>
    );
  }

  return (
    <BackContainer onBackClick={props.onBackClick}>
      <DynamicReport id={props.reportId} />;
    </BackContainer>
  );
};
